import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaTwitch, FaTrophy, FaCheck, FaTimes } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader/PageHeader';
import { Skeleton } from '../../components/Skeleton/Skeleton';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import gamesService from '../../services/gamesService';
import PenaltyScene, { zoneRect } from './PenaltyScene';
import { ZONES, ZONE_LABELS, takeShot } from './penalties';
import './PenaltisPage.css';

const FLIGHT_MS = 540;
const RESULT_MS = 1600;
const PRACTICE_BEST_KEY = 'jizon.penaltis.best';

// Full stadium on desktop; tighter crop around the goal on phones
const VIEW_WIDE = { x: 220, y: 190, w: 1160, h: 770 };
const VIEW_NARROW = { x: 300, y: 230, w: 1000, h: 760 };
const NARROW_QUERY = '(max-width: 640px)';

function readPracticeBest() {
  try {
    return Number(localStorage.getItem(PRACTICE_BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writePracticeBest(value) {
  try {
    localStorage.setItem(PRACTICE_BEST_KEY, String(value));
  } catch {
    // storage unavailable (private mode): the record just isn't kept
  }
}

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(NARROW_QUERY).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

// Position an HTML hit-area over a zone, in % of the visible viewBox
function zoneStyle(zone, view) {
  const r = zoneRect(zone);
  return {
    left: `${((r.x - view.x) / view.w) * 100}%`,
    top: `${((r.y - view.y) / view.h) * 100}%`,
    width: `${(r.w / view.w) * 100}%`,
    height: `${(r.h / view.h) * 100}%`,
  };
}

function Leaderboard({ data, loading, isAuthenticated }) {
  return (
    <aside className="pk-board" aria-label="Ranking">
      <div className="pk-board-head">
        <FaTrophy className="pk-board-icon" />
        <h2>Ranking</h2>
        <span>Top 10</span>
      </div>

      {loading ? (
        <ol className="pk-board-list">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i} className="pk-board-row">
              <Skeleton width={18} height={12} />
              <Skeleton width={26} height={26} radius={999} />
              <Skeleton width="50%" height={12} />
            </li>
          ))}
        </ol>
      ) : data?.entries?.length ? (
        <ol className="pk-board-list">
          {data.entries.map(entry => (
            <li key={entry.rank} className={`pk-board-row ${entry.isMe ? 'is-me' : ''} rank-${entry.rank}`}>
              <span className="pk-board-rank">{entry.rank}</span>
              {entry.avatarUrl
                ? <img className="pk-board-avatar" src={entry.avatarUrl} alt="" />
                : <span className="pk-board-avatar pk-board-avatar-empty" />}
              <span className="pk-board-name">{entry.displayName}</span>
              <span className="pk-board-goals">{entry.goals}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="pk-board-empty">Ainda ninguém marcou. Sê o primeiro!</p>
      )}

      {isAuthenticated ? (
        data?.me && (
          <p className="pk-board-me">
            O teu recorde: <strong>{data.me.best}</strong>
            {data.me.rank && <> · <strong>{data.me.rank}.º</strong> lugar</>}
          </p>
        )
      ) : (
        <div className="pk-board-cta">
          <p>Entra com a Twitch para os teus golos contarem para o ranking.</p>
          <button className="btn btn-primary" onClick={() => authService.loginWithTwitch()}>
            <FaTwitch />
            Entrar com a Twitch
          </button>
        </div>
      )}
    </aside>
  );
}

function PenaltisPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const ranked = isAuthenticated;
  const narrow = useIsNarrow();
  const view = narrow ? VIEW_NARROW : VIEW_WIDE;

  // idle -> aiming -> shooting -> (aiming | over)
  const [phase, setPhase] = useState('idle');
  const [goals, setGoals] = useState(0);
  const [best, setBest] = useState(0);
  const [shot, setShot] = useState(null); // { zone, keeperZone, goal }
  const [history, setHistory] = useState([]); // { zone, goal } per shot this run
  const [hoverZone, setHoverZone] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [newRecord, setNewRecord] = useState(false);
  const [error, setError] = useState(null);
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const timers = useRef([]);
  const bestAtStart = useRef(0);

  const later = (fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const loadBoard = useCallback(async () => {
    try {
      const data = await gamesService.getPenaltyLeaderboard();
      setBoard(data);
      if (data.me) setBest(prev => Math.max(prev, data.me.best));
    } catch {
      setBoard(null);
    } finally {
      setBoardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!ranked) setBest(readPracticeBest());
    loadBoard();
  }, [authLoading, ranked, loadBoard]);

  const start = async () => {
    setError(null);
    setShot(null);
    setHistory([]);
    setShowResult(false);
    setNewRecord(false);
    try {
      if (ranked) {
        const data = await gamesService.startPenaltyRun();
        setBest(data.best);
        bestAtStart.current = data.best;
      } else {
        bestAtStart.current = best;
      }
      setGoals(0);
      setPhase('aiming');
    } catch (e) {
      setError(e.message);
    }
  };

  const shoot = async (zone) => {
    if (phase !== 'aiming') return;
    setPhase('shooting');
    setHoverZone(null);
    setError(null);

    let outcome;
    try {
      if (ranked) {
        const data = await gamesService.shootPenalty(zone);
        outcome = { keeperZone: data.keeperZone, goal: data.goal, goals: data.goals, best: data.best };
      } else {
        const { keeperZone, goal } = takeShot(zone, goals);
        const nextGoals = goal ? goals + 1 : goals;
        outcome = { keeperZone, goal, goals: nextGoals, best: Math.max(best, nextGoals) };
      }
    } catch (e) {
      setError(e.message);
      setPhase('aiming');
      return;
    }

    setShot({ zone, keeperZone: outcome.keeperZone, goal: outcome.goal });

    later(() => {
      setShowResult(true);
      setGoals(outcome.goals);
      setBest(outcome.best);
      setHistory(prev => [...prev, { zone, goal: outcome.goal }]);
    }, FLIGHT_MS);

    later(() => {
      setShowResult(false);
      if (outcome.goal) {
        setShot(null);
        setPhase('aiming');
        return;
      }
      setNewRecord(outcome.goals > bestAtStart.current);
      if (!ranked) writePracticeBest(outcome.best);
      setPhase('over');
      loadBoard();
    }, FLIGHT_MS + RESULT_MS);
  };

  const myRank = ranked ? board?.me?.rank : null;

  return (
    <main className="page-container penaltis-page">
      <PageHeader
        eyebrow="Jogo"
        title="Penáltis"
        subtitle="Escolhe o canto e bate o guarda-redes. Remata até falhar: cada golo conta e o guarda-redes fica mais difícil."
      />

      <div className="pk-layout">
        <section className="pk-game">
          <div className="pk-machine">
            <div
              className={`pk-stage phase-${phase}`}
              style={{ aspectRatio: `${view.w} / ${view.h}` }}
            >
              <PenaltyScene
                shot={shot}
                showResult={showResult}
                viewBox={view}
                hoverZone={hoverZone}
                aiming={phase === 'aiming'}
              />

              {!authLoading && (
                <span className={`pk-mode ${ranked ? 'is-ranked' : ''}`}>
                  <span className="pk-mode-dot" />
                  {ranked ? 'Ranking' : 'Treino'}
                </span>
              )}

              <div className="pk-zones" role="group" aria-label="Escolhe onde rematar">
                {Array.from({ length: ZONES }).map((_, zone) => (
                  <button
                    key={zone}
                    className="pk-zone"
                    style={zoneStyle(zone, view)}
                    onClick={() => shoot(zone)}
                    onMouseEnter={() => setHoverZone(zone)}
                    onMouseLeave={() => setHoverZone(z => (z === zone ? null : z))}
                    onFocus={() => setHoverZone(zone)}
                    onBlur={() => setHoverZone(z => (z === zone ? null : z))}
                    disabled={phase !== 'aiming'}
                    aria-label={ZONE_LABELS[zone]}
                  />
                ))}
              </div>

              {showResult && shot && (
                <div className={`pk-result ${shot.goal ? 'is-goal' : 'is-saved'}`} role="status">
                  <span className="pk-result-title">{shot.goal ? 'Golo!' : 'Defendido'}</span>
                  <span className="pk-result-sub">
                    {shot.goal ? `${goals} ${goals === 1 ? 'golo seguido' : 'golos seguidos'}` : 'Fim da série'}
                  </span>
                </div>
              )}

              {(phase === 'idle' || phase === 'over') && (
                <div className="pk-overlay">
                  <div className="pk-overlay-panel">
                    {phase === 'idle' ? (
                      <>
                        <span className="pk-overlay-eyebrow">Morte súbita</span>
                        <h2>Bate o guarda-redes</h2>
                        <p>Escolhe um dos 6 cantos. Cada golo soma um ponto e o guarda-redes fica mais difícil. Um falhanço e acabou.</p>
                      </>
                    ) : (
                      <>
                        <span className={`pk-overlay-eyebrow ${newRecord ? 'is-record' : ''}`}>
                          {newRecord ? 'Novo recorde' : 'Fim da série'}
                        </span>
                        <span className="pk-final-value">{goals}</span>
                        <span className="pk-final-label">{goals === 1 ? 'golo seguido' : 'golos seguidos'}</span>
                      </>
                    )}
                    <button className="pk-play" onClick={start} disabled={authLoading}>
                      {phase === 'idle' ? 'Jogar' : 'Jogar outra vez'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Control bar attached to the stage */}
            <div className="pk-bar" aria-live="polite">
              <dl className="pk-bar-stats">
                <div>
                  <dt>Golos seguidos</dt>
                  <dd className="is-main">{goals}</dd>
                </div>
                <div>
                  <dt>Recorde</dt>
                  <dd>{best}</dd>
                </div>
                <div>
                  <dt>Ranking</dt>
                  <dd>{myRank ? `${myRank}.º` : '—'}</dd>
                </div>
              </dl>

              <ol className="pk-bar-history" aria-label="Remates desta série">
                {history.slice(-10).map(({ zone, goal }, i) => (
                  <li
                    key={i}
                    className={`pk-shot ${goal ? 'is-goal' : 'is-miss'}`}
                    title={`${goal ? 'Golo' : 'Defendido'} · ${ZONE_LABELS[zone]}`}
                  >
                    {goal ? <FaCheck /> : <FaTimes />}
                  </li>
                ))}
                {Array.from({ length: Math.max(0, 5 - history.length) }).map((_, i) => (
                  <li key={`empty-${i}`} className="pk-shot is-empty" />
                ))}
              </ol>

              <div className="pk-bar-action">
                {phase === 'aiming' && <span className="pk-bar-hint">Escolhe um canto da baliza</span>}
                {phase === 'shooting' && <span className="pk-bar-hint">A rematar…</span>}
              </div>
            </div>
          </div>

          {error && <p className="pk-error" role="alert">{error}</p>}
        </section>

        <Leaderboard data={board} loading={boardLoading} isAuthenticated={isAuthenticated} />
      </div>
    </main>
  );
}

export default PenaltisPage;
